/**
 * SiteVaultDownloads — founder-only · /admin/system/sitevault.
 *
 * Lists the planning documents in the SiteVault mirror and lets the
 * founder click-to-download them. Uses the axios `api` client (which
 * already carries the admin JWT) so a browser session is all that's
 * needed — no manual curl required.
 *
 * Purely a read-only download UI · NOT spine code · does NOT violate
 * the Admin 1b/1c/1d stop rule.
 */
import React, { useEffect, useState, useContext } from "react";
import { api, formatApiError } from "@/lib/api";
import { OSContext } from "@/pages/admin/os/ConciergeOSShell";

function fmtSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} kB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function extLabel(name) {
  const ext = name.split(".").pop().toUpperCase();
  return ext.length <= 4 ? ext : "FILE";
}

export default function SiteVaultDownloads() {
  const { me } = useContext(OSContext);
  const [listing, setListing] = useState(null);
  const [err, setErr] = useState("");
  const [downloading, setDownloading] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/os-admin/sitevault/files");
        setListing(data);
      } catch (e) {
        setErr(formatApiError(e.response?.data?.detail) || e.message);
      }
    })();
  }, []);

  const isFounder = me?.os_role === "founder";

  if (me && !isFounder) {
    return (
      <div data-testid="os-sitevault-locked">
        <div className="os-pagehead"><h1>SiteVault · Planning Documents</h1></div>
        <div className="os-placeholder">
          <span className="tag">Founder only</span>
          <h2>Planning-document download is restricted</h2>
          <p>
            Only the Founder role may download the SiteVault planning documents.
            Your current role is <code style={{ color: "var(--os-brass-bright)" }}>{me?.os_role || "none"}</code>.
          </p>
        </div>
      </div>
    );
  }

  const downloadFile = async (filename) => {
    setDownloading(filename);
    setErr("");
    try {
      const res = await api.get(`/os-admin/sitevault/file/${encodeURIComponent(filename)}`, {
        responseType: "blob",
      });
      // Browsers reject Content-Disposition cross-origin on XHR, so we
      // create a blob URL and click a synthetic anchor.
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setDownloading("");
    }
  };

  return (
    <div data-testid="os-sitevault-downloads">
      <div className="os-pagehead">
        <h1>SiteVault · Planning Documents</h1>
        <span className="count">{listing ? `${listing.files.length} files` : "loading…"}</span>
      </div>
      <div className="os-pagesub">
        Core planning documents mirrored alongside the database. Click to download — your admin session token is used automatically. Founder only.
      </div>

      {err && (
        <div
          style={{
            background: "rgba(199,106,91,0.08)",
            border: "1px solid rgba(199,106,91,0.4)",
            color: "#E5B6AE",
            padding: "10px 14px",
            borderRadius: 6,
            marginBottom: 16,
            fontSize: 12.5,
          }}
          data-testid="os-sitevault-error"
        >
          {err}
        </div>
      )}

      <table className="os-mig-table" data-testid="os-sitevault-table">
        <thead>
          <tr>
            <th style={{ width: "8%" }}>Type</th>
            <th>File</th>
            <th style={{ width: "12%" }}>Size</th>
            <th style={{ width: "14%" }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {listing?.files.map((f) => (
            <tr key={f.name} data-testid={`os-sitevault-row-${f.name}`}>
              <td>
                <span className="os-pill" style={{ borderColor: "var(--os-hair)", color: "var(--os-brass-bright)", background: "rgba(194,160,99,0.08)" }}>
                  {extLabel(f.name)}
                </span>
              </td>
              <td className="os-ref">{f.name}</td>
              <td style={{ color: "var(--os-muted)", fontSize: 11.5, fontFamily: "var(--os-font-mono)" }}>
                {fmtSize(f.size)}
              </td>
              <td>
                <button
                  className="os-btn-ghost"
                  disabled={downloading === f.name}
                  onClick={() => downloadFile(f.name)}
                  data-testid={`os-sitevault-download-${f.name}`}
                  style={{
                    cursor: downloading === f.name ? "wait" : "pointer",
                    color: "var(--os-brass-bright)",
                    borderColor: "var(--os-brass)",
                  }}
                >
                  {downloading === f.name ? "downloading…" : "↓ Download"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <p
        style={{
          fontSize: 11.5,
          color: "var(--os-faint)",
          marginTop: 16,
          fontStyle: "italic",
          maxWidth: 760,
        }}
      >
        Read-only mirror of <code style={{ color: "var(--os-brass-bright)" }}>{listing?.root || "/app/memory/SiteVault/ConciergeOS"}</code>.
        Each file rides the daily SiteVault dump alongside the database. Founder-gated · backend re-validates the role on every download.
      </p>
    </div>
  );
}
